import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppShell, Content } from '../components/ui/AppShell';
import { Placeholder } from '../components/ui/Placeholder';
import { Trophy } from '../components/ui/icons';
import { LeagueSwitcher } from '../components/ui/LeagueSwitcher';
import { useHaptics } from '../hooks/useHaptics';
import { useAuth } from '../context/AuthContext';
import {
  useLeagues,
  useMyMembership,
  useCurrentRound,
  useMyEntry,
  useMySeasonResults,
  useLeagueRounds,
  useLeagueSeason,
  useFriendsInLeague,
  useRoundFixtures
} from '../hooks/useQueries';
import { identityFor } from '../data/leagues';
import { StandingsSegment, type StandingsView } from '../components/standings/StandingsSegment';
import { SegmentedViews } from '../components/ui/SegmentedViews';
import {
  DivisionDatedView,
  FriendsStandingsView,
  LeagueSeasonStandingsView,
  SeasonStandingsView
} from '../components/standings/views';

function parseView(raw: string | null): StandingsView {
  if (raw === 'league' || raw === 'season' || raw === 'friends') return raw;
  return 'live';
}

export default function Standings() {
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();
  const [params, setParams] = useSearchParams();
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: leagues } = useLeagues();

  const activeView = parseView(params.get('view'));

  const league = leagues?.find((l) => l.leagueId === leagueId);
  const identity = identityFor(leagueId ?? '');
  const leagueName = league?.name ?? identity?.name ?? 'your league';
  const roundId = league?.currentRoundId ?? null;

  const { data: round } = useCurrentRound(leagueId, roundId);
  const { data: membership } = useMyMembership(leagueId, uid);
  const { data: entry } = useMyEntry(leagueId, roundId, uid);
  const roundOpen = round?.status === 'OPEN';

  // Live signal for the Division view: a round still in play (OPEN/LOCKING/SETTLING)
  // is provisional, so we show how many of its matches have actually finished. A
  // live listener keeps the count moving as results land.
  const isLiveRound =
    round?.status === 'OPEN' || round?.status === 'LOCKING' || round?.status === 'SETTLING';
  const { data: liveFixtures } = useRoundFixtures(leagueId, roundId, isLiveRound);
  const settledFixtureCount = (liveFixtures ?? []).filter(
    (f) => f.status === 'SETTLED' || f.status === 'VOID'
  ).length;
  const liveProgress =
    isLiveRound && (liveFixtures?.length ?? 0) > 0
      ? { settled: settledFixtureCount, total: liveFixtures!.length }
      : null;
  const { data: seasonResults } = useMySeasonResults(leagueId, uid);
  const { data: rounds } = useLeagueRounds(leagueId);
  const { data: friends } = useFriendsInLeague(uid, leagueId);
  const { data: leagueSeason } = useLeagueSeason(leagueId, uid);

  const header = useMemo(() => {
    switch (activeView) {
      case 'league':
        return { eyebrow: league ? `Season ${league.season} · everyone` : 'Leaderboard' };
      case 'season':
        return { eyebrow: league ? `Season ${league.season}` : 'Season' };
      case 'friends':
        return { eyebrow: 'Among friends' };
      default:
        return { eyebrow: round?.label ?? leagueName };
    }
  }, [activeView, league, round?.label, leagueName]);

  const setView = (view: StandingsView) => {
    impact('selection');
    const next = new URLSearchParams(params);
    if (view === 'live') next.delete('view');
    else next.set('view', view);
    setParams(next, { replace: true });
  };

  return (
    <AppShell>
      <Content width="wide">
        <div className="pt-2 md:pt-5">
          <LeagueSwitcher leagueId={leagueId!} tab="standings" size="sm" />
        </div>

        <div className="pt-3">
          <div className="eyebrow font-bold">{header.eyebrow}</div>
          <h1 className="title mt-[6px] text-[30px] md:text-[40px]">Standings</h1>
        </div>

        <div className="pt-4">
          <StandingsSegment active={activeView} onChange={setView} />
          {activeView === 'live' && (
            <p className="mt-2 px-0.5 text-[11.5px] font-medium leading-[1.45] text-ink3">
              Your group for each round — everyone ranked by round score. Green rows promote, red rows relegate.
            </p>
          )}
          {activeView === 'season' && (
            <p className="mt-2 px-0.5 text-[11.5px] font-medium leading-[1.45] text-ink3">
              Every settled round with why you moved — tap a row for the full division table and card breakdown.
            </p>
          )}
        </div>

        <SegmentedViews
          value={activeView}
          order={['live', 'league', 'season', 'friends']}
          className="flex flex-col gap-4 pt-[18px]"
        >
          {activeView === 'live' && uid && (
            <DivisionDatedView
              leagueId={leagueId}
              uid={uid}
              currentRound={round}
              membershipTier={membership?.tier}
              isCycling={league?.sport === 'CYCLING'}
              liveProgress={liveProgress}
              roundOpen={roundOpen}
              hasEntry={!!entry}
              onOpenRound={(rid) => {
                impact('medium');
                navigate(`/play/${leagueId}/results?round=${rid}`);
              }}
              onEnterRound={() => {
                impact('medium');
                navigate(`/play/${leagueId}`);
              }}
            />
          )}

          {activeView === 'league' && (
            <LeagueSeasonStandingsView rows={leagueSeason ?? []} leagueName={leagueName} />
          )}

          {activeView === 'season' &&
            (league && membership ? (
              <SeasonStandingsView
                league={league}
                membership={membership}
                results={seasonResults ?? []}
                totalRounds={rounds?.length ?? 0}
                onOpen={(rid) => {
                  impact('medium');
                  navigate(`/play/${leagueId}/results?round=${rid}`);
                }}
              />
            ) : (
              <Placeholder
                Icon={Trophy}
                title="Season recap loading"
                body="Your season points, tier journey and round history appear here."
              />
            ))}

          {activeView === 'friends' && (
            <FriendsStandingsView
              rows={friends ?? []}
              leagueName={leagueName}
              onInvite={() => {
                impact('light');
                navigate(`/play/${leagueId}/friends`);
              }}
            />
          )}
        </SegmentedViews>
      </Content>
    </AppShell>
  );
}
